// Calculate ward bounds for zooming
export const calculateWardBounds = (ward) => {
  if (!ward || !ward.geometry) return null;
  
  const geometry = ward.geometry;
  let minLon = Infinity, maxLon = -Infinity;
  let minLat = Infinity, maxLat = -Infinity;
  
  function processCoordinates(coords) {
    if (Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
      coords.forEach(ring => processCoordinates(ring));
    } else if (Array.isArray(coords[0]) && typeof coords[0][0] === 'number') {
      coords.forEach(coord => {
        const [lon, lat] = coord;
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      });
    }
  }
  
  if (geometry.type === 'Polygon') {
    processCoordinates(geometry.coordinates);
  } else if (geometry.type === 'MultiPolygon') {
    geometry.coordinates.forEach(polygon => processCoordinates(polygon));
  }
  
  if (minLon === Infinity) return null;
  
  return {
    minLon, maxLon, minLat, maxLat,
    centerLon: (minLon + maxLon) / 2,
    centerLat: (minLat + maxLat) / 2
  };
};

// Calculate zoom level based on bounds
export const calculateZoomFromBounds = (bounds) => {
  if (!bounds) return 12;
  
  const latDiff = bounds.maxLat - bounds.minLat;
  const lonDiff = bounds.maxLon - bounds.minLon;
  const maxDiff = Math.max(latDiff, lonDiff);
  
  let zoom = 12;
  if (maxDiff > 0.1) zoom = 11;
  if (maxDiff > 0.2) zoom = 10;
  if (maxDiff < 0.05) zoom = 13;
  if (maxDiff < 0.02) zoom = 14;
  
  return zoom;
};

// Calculate city bounds from GeoJSON
export const calculateCityBounds = (geojson) => {
  let minLon = Infinity, maxLon = -Infinity;
  let minLat = Infinity, maxLat = -Infinity;
  
  geojson.features.forEach(feature => {
    const coords = feature.geometry.coordinates;
    function processCoordsForBounds(coordArray) {
      coordArray.forEach(item => {
        if (Array.isArray(item[0])) {
          processCoordsForBounds(item);
        } else {
          const [lon, lat] = item;
          minLon = Math.min(minLon, lon);
          maxLon = Math.max(maxLon, lon);
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
        }
      });
    }
    processCoordsForBounds(coords);
  });
  
  const centerLon = (minLon + maxLon) / 2;
  const centerLat = (minLat + maxLat) / 2;
  
  const latDiff = maxLat - minLat;
  const lonDiff = maxLon - minLon;
  const maxDiff = Math.max(latDiff, lonDiff);
  
  let fitZoom = 10;
  if (maxDiff > 0.5) fitZoom = 8;
  else if (maxDiff > 0.3) fitZoom = 9;
  else if (maxDiff > 0.15) fitZoom = 10;
  else fitZoom = 11;
  
  return {
    center: { lat: centerLat, lon: centerLon },
    zoom: fitZoom
  };
};

// Determine ward property name from GeoJSON
export const getWardPropertyName = (geojson) => {
  if (!geojson.features || geojson.features.length === 0) return 'ward_number';
  
  const firstFeature = geojson.features[0];
  if (firstFeature.properties.ward_number) return 'ward_number';
  if (firstFeature.properties.Ward_Number) return 'Ward_Number';
  if (firstFeature.properties.Ward_No) return 'Ward_No';
  if (firstFeature.properties.WARD_NO) return 'WARD_NO';
  if (firstFeature.properties.ward_no) return 'ward_no';
  
  return 'ward_number';
};

// Extract ward number from feature
export const extractWardNumber = (feature) => {
  if (!feature.properties) return null;
  
  const wardNoStr = feature.properties.Ward_No || 
                   feature.properties.WARD_NO ||
                   feature.properties.ward_no || 
                   feature.properties.Ward_Number || 
                   feature.properties.ward_number;
  
  if (!wardNoStr) return null;
  
  const numericMatch = wardNoStr.toString().match(/^(\d+)$/);
  if (numericMatch) {
    return parseInt(numericMatch[0], 10);
  }
  
  const specialMatch = wardNoStr.toString().match(/\d+/);
  if (specialMatch) {
    return parseInt(specialMatch[0], 10);
  }
  
  return null;
};

